import { useEffect, useState, useCallback, useRef } from 'react';
import './SMRTHost.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput from '../components/PhoneInput';
import SmsConsentModal, { HOST_WAITLIST_CONSENT_TEXT } from '../components/SmsConsentModal';

type Entry = {
  id: string;
  orgID: string;

  guest: {
    name: string;
    phone: string;
    partySize: number;
  };
  preferredArea: string;

  businessDate: string;

  createdAt: number;
  notifiedAt?: number[];
  cancelledAt?: number;
  restoredAt?: number;

  seatedAt?: number;
  seatedArea?: string;

  status: "waiting" | "notified" | "cancelled" | "seated";
  cancelledBy?: "guest"|"staff"|"system";
};

type SortKey = 'waiting' | 'party' | 'name';

export default function SMRTHost() {
  const [view, setView] = useState<'current' | 'history' | 'tables'>('current');
  const [shakeButtons, setShakeButtons] = useState<Record<string, boolean>>({});

  const [entries, setEntries] = useState<Entry[]>([]);
  const [history, setHistory] = useState<Entry[]>([]);

  const [selectedDate, setSelectedDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10)
  );

  const [sortBy, setSortBy] = useState<SortKey>('waiting');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [showPhone, setShowPhone] = useState<string | null>(null);

  const [lastNotified, setLastNotified] = useState<Record<string, number>>({});
  const [notifyInFlight, setNotifyInFlight] = useState<Record<string, boolean>>({});

  const [now, setNow] = useState(Date.now());

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [area, setArea] = useState('Main Bar');

  const [seatConfirmId, setSeatConfirmId] = useState<string | null>(null);
  const [seatArea, setSeatArea] = useState('Main Bar');

  // ── Guest SMS consent (Twilio A2P 10DLC) ─────────────────────────────────
  // Never pre-accepted: the modal opens unconfirmed on every Add Guest, and the
  // guest is not created until staff confirm.
  const [showSmsConsent, setShowSmsConsent] = useState(false);
  const consentSubmittingRef = useRef(false);

  // ---------- sorting ----------
  const sortedEntries = [...entries].sort((a, b) => {
    const waitingDiff = a.createdAt - b.createdAt;

    if (sortBy === 'party') {
      const diff = a.guest.partySize - b.guest.partySize;
      return diff !== 0 ? (sortDir === 'asc' ? diff : -diff) : waitingDiff;
    }

    if (sortBy === 'name') {
      const diff = a.guest.name.localeCompare(b.guest.name);
      return diff !== 0 ? (sortDir === 'asc' ? diff : -diff) : waitingDiff;
    }

    return sortDir === 'asc' ? waitingDiff : -waitingDiff;
  });

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  // ---------- helpers ----------
  const getNotifyRemainingSeconds = (id: string) => {
    const last = lastNotified[id];
    if (!last) return 0;
    return Math.max(0, Math.ceil((60000 - (now - last)) / 1000));
  };

  // ---------- load ----------
  const loadCurrent = useCallback(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/waitlist`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setEntries(Array.isArray(d) ? d : []))
      .catch(() => setEntries([]));
  },[]);

  const loadHistory = useCallback(() => {
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/waitlist/history?date=${selectedDate}`,
      { credentials: 'include' }
    )
      .then(r => r.json())
      .then(d => setHistory(Array.isArray(d) ? d : []))
      .catch(() => setHistory([]));
  }, [selectedDate]);

  useEffect(() => {
    loadCurrent();
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [loadCurrent]);

  useEffect(() => {
    if (view === 'history') {
      loadHistory();
    }
  }, [selectedDate, loadHistory, view]);



  // ---------- actions ----------
  // "Add Guest" no longer adds: it validates and opens the consent modal. The
  // guest is only created once staff click "Confirm & Add Guest".
  const submit = () => {
    if (!name.trim()) return alert('Please enter a guest name.');
    if (!phone) return alert('Please enter a valid phone number.');

    const parsed = parsePhoneNumberFromString(phone);
    if (!parsed?.isValid()) return alert('Please enter a valid phone number.');

    setShowSmsConsent(true);
  };

  const confirmAddGuest = async () => {
    // Guard against double-clicks: the ref flips synchronously, before React
    // re-renders the disabled button.
    if (consentSubmittingRef.current) return;
    consentSubmittingRef.current = true;

    const parsed = parsePhoneNumberFromString(phone);
    if (!parsed?.isValid()) {
      consentSubmittingRef.current = false;
      setShowSmsConsent(false);
      return alert('Please enter a valid phone number.');
    }

    setShowSmsConsent(false);

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/waitlist/checkin`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone: parsed.number, partySize, area, smsConsent: true }),
      });
    } finally {
      consentSubmittingRef.current = false;
    }

    setName('');
    setPhone('');
    setPartySize(2);
    setArea('Main Bar');
    loadCurrent();
  };

  const action = async (id: string, type: 'notify' | 'cancel' | 'restore') => {
    const key = `${id}-${type}`; // unique per button

    if (type === 'notify') {
      if (getNotifyRemainingSeconds(id) > 0) return;

      setNotifyInFlight(p => ({ ...p, [id]: true }));

      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/waitlist/${id}/notify`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Notify failed');

        setLastNotified(p => ({ ...p, [id]: Date.now() }));
      } catch (err) {
        // Trigger shake animation
        setShakeButtons(p => ({ ...p, [key]: true }));
        setTimeout(() => {
          setShakeButtons(p => ({ ...p, [key]: false }));
        }, 400);
      } finally {
        setNotifyInFlight(p => ({ ...p, [id]: false }));
        loadCurrent();
      }
      return;
    }

    // cancel or restore
    const url =
      type === 'cancel'
        ? `${process.env.REACT_APP_BACKEND_URL}/waitlist/${id}/cancel`
        : `${process.env.REACT_APP_BACKEND_URL}/waitlist/${id}/restore`;

    try {
      const res = await fetch(url, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error(`${type} failed`);
    } catch (err) {
      // Trigger shake for cancel/restore
      setShakeButtons(p => ({ ...p, [key]: true }));
      setTimeout(() => {
        setShakeButtons(p => ({ ...p, [key]: false }));
      }, 400);
    } finally {
      loadCurrent();
      loadHistory();
    }
  };


  const confirmSeat = async (id: string) => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/waitlist/${id}/seat`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ seatedArea: seatArea }),
    });
    setSeatConfirmId(null);
    loadCurrent();
    loadHistory();
  };

  // ---------- render ----------
  return (
    <div className="smrt-host-layout">
      <div className="smrt-host-card">
        <h3>Add Guest</h3>

        <input placeholder="Guest name" value={name} onChange={e => setName(e.target.value)} />

        <PhoneInput
          phone={phone}
          setPhone={setPhone}
        />


        <input type="number" min={1} value={partySize} onChange={e => setPartySize(+e.target.value)} />

        <select value={area} onChange={e => setArea(e.target.value)}>
          <option>Main Bar</option>
          <option>Patio</option>
          <option>Parlour</option>
        </select>

        <button onClick={submit}>Add Guest</button>
      </div>

      <div className="smrt-host-table">
        {/* Canonical tab pattern (matches Profiles / SMRT Docs / SMRT COP).
            smrtcop-tab styles are loaded globally via App.js. */}
        <div className="smrtcop-tabs" style={{ marginBottom: 12 }}>
          <div
            className={`smrtcop-tab${view === 'current' ? ' smrtcop-tab-active' : ''}`}
            onClick={() => setView('current')}
          >
            Current Guests
          </div>
          <div
            className={`smrtcop-tab${view === 'history' ? ' smrtcop-tab-active' : ''}`}
            onClick={() => setView('history')}
          >
            Daily History
          </div>
          <div
            className="smrtcop-tab"
            style={{ opacity: 0.5, cursor: 'not-allowed' }}
            title="Coming soon"
          >
            Table View
          </div>
        </div>

        {view === 'history' && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ marginRight: 8, fontWeight: 600 }}>Business date:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
            />
          </div>
        )}

        {view === 'current' && (
          <table>
            <thead>
              <tr>
                <th>
                  Name
                  <button className={`sort-btn ${sortBy === 'name' ? 'active' : ''}`} onClick={() => toggleSort('name')}>
                    {sortBy === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
                  </button>
                </th>
                <th>
                  Party
                  <button className={`sort-btn ${sortBy === 'party' ? 'active' : ''}`} onClick={() => toggleSort('party')}>
                    {sortBy === 'party' ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
                  </button>
                </th>
                <th>Area</th>
                <th>
                  Waiting
                  <button className={`sort-btn ${sortBy === 'waiting' ? 'active' : ''}`} onClick={() => toggleSort('waiting')}>
                    {sortBy === 'waiting' ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
                  </button>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map(e => (
                <tr key={e.id}>
                  <td>
                    <span onClick={() => setShowPhone(showPhone === e.id ? null : e.id)} style={{ cursor: 'pointer', fontWeight: 600 }}>
                      {e.guest.name}
                    </span>
                    {showPhone === e.id && <div className="phone">{e.guest.phone}</div>}
                  </td>
                  <td>{e.guest.partySize}</td>
                  <td>{e.preferredArea}</td>
                  <td>
                    {Math.floor((now - e.createdAt) / 60000)} min
                    {now - e.createdAt > 20 * 60 * 1000 && (
                      <span style={{ marginLeft: 6, fontWeight: 700, color: '#d97706' }}>⚠️</span>
                    )}
                  </td>
                  <td className="actions">
                    <button className={`notify ${shakeButtons[`${e.id}-notify`] ? 'shake' : ''}`} disabled={getNotifyRemainingSeconds(e.id) > 0} onClick={() => action(e.id, 'notify')}>
                      {getNotifyRemainingSeconds(e.id) > 0 ? `Notify (${getNotifyRemainingSeconds(e.id)}s)` : 'Notify'}
                    </button>
                    {seatConfirmId === e.id ? (
                      <>
                        <select value={seatArea} onChange={e => setSeatArea(e.target.value)}>
                          <option>Main Bar</option>
                          <option>Patio</option>
                          <option>Parlour</option>
                        </select>
                        <button className={`seat ${shakeButtons[`${e.id}-restore`] ? 'shake' : ''}`} onClick={() => confirmSeat(e.id)}>Confirm</button>
                      </>
                    ) : (
                      <button className="seat" onClick={() => { setSeatConfirmId(e.id); setSeatArea(e.preferredArea); }}>Seat</button>
                    )}
                    <button className={`remove ${shakeButtons[`${e.id}-cancel`] ? 'shake' : ''}`} disabled={notifyInFlight[e.id]} onClick={() => action(e.id, 'cancel')}>
                      Cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {view === 'history' && (
          history.length === 0 ? (
            <p>No history for this date.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Party</th>
                  <th>Area</th>
                  <th>Status</th>
                  <th>Wait (min)</th>
                  <th>Time</th>
                  <th>Notified</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map(e => (
                  <tr key={e.id}>
                    <td>{e.guest.name}</td>
                    <td>{e.guest.phone}</td>
                    <td>{e.guest.partySize}</td>
                    <td>{e.seatedArea || e.preferredArea}</td>
                    <td>{e.status}</td>
                    <td>
                      {Math.max(
                        0,
                        Math.round(
                          (
                            (e.status === 'seated'
                              ? e.seatedAt!
                              : e.cancelledAt!) - e.createdAt
                          ) / 60000
                        )
                      )}
                    </td>

                    <td>{new Date(e.status === 'seated' ? e.seatedAt! : e.cancelledAt!).toLocaleTimeString()}</td>
                    <td>{e.notifiedAt?.length || 0}</td>
                    <td>{e.status === 'cancelled' && <button onClick={() => action(e.id, 'restore')}>Restore</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      <SmsConsentModal
        show={showSmsConsent}
        onCancel={() => setShowSmsConsent(false)}
        onAgree={confirmAddGuest}
        title="Confirm SMS Notification Consent"
        consentText={HOST_WAITLIST_CONSENT_TEXT}
        confirmLabel="Confirm & Add Guest"
      />
    </div>
  );
}
